import React, { useContext, useState } from 'react';
import { MetadataContext, ResponsiveImage } from '@/components';
import { ImageModel } from '@/types';
import classNames from 'classnames';
import Play from '@/assets/play.svg';
import EmbeddedMediaElement from '../media/EmbeddedMediaElement';

interface FullPageEmbeddedMediaElementProps extends React.HTMLAttributes<HTMLDivElement> {
  videoEmbedCode?: string;
  videoUrl?: string;
  coverImage?: {
    desktopImage: ImageModel;
    mobileImage?: ImageModel;
  };
  useHeroImageHeightForEmbedVideo?: boolean;
  playIcon?: React.ReactNode;
  onPlayClicked?: () => void;
}

const FullPageEmbeddedMediaElement = ({
  videoEmbedCode,
  videoUrl,
  coverImage,
  useHeroImageHeightForEmbedVideo,
  className,
  playIcon,
  onPlayClicked,
  ...props
}: FullPageEmbeddedMediaElementProps) => {
  const [playClicked, setPlayClicked] = useState(false);

  const { pageType } = useContext(MetadataContext);
  const videoType: string = videoEmbedCode?.includes('youtube') ? 'youtube' : 'other';

  let youtubeVideoUrl,
    youtubeVideoId = undefined;

  if (videoType === 'youtube' && videoEmbedCode) {
    const regExp =
      /(?:https?:\/\/(?:www\.)?)?(?:youtube(?:-nocookie)?\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]vi?=)|youtu\.be\/)([^"&?\/ ]{11}).*/;
    const executionResult = regExp.exec(videoEmbedCode);
    if (executionResult !== null) {
      youtubeVideoId = executionResult[1];
      youtubeVideoUrl = `https://www.youtube.com/embed/${youtubeVideoId}?controls=0&autoplay=1&enablejsapi=1`;
    } else {
      console.warn(`[vue-lazy-youtube-video]: Failed to extract video id from "${videoEmbedCode}"`);
    }
  }

  const _onPlayClicked = () => {
    if (onPlayClicked) onPlayClicked();
    setPlayClicked(true);
  };

  return (
    <div
      className={classNames(
        'FullPageEmbeddedMediaElement relative',
        !useHeroImageHeightForEmbedVideo && (videoEmbedCode || videoUrl || youtubeVideoUrl) && 'h-[50vh]',
        className,
      )}
      {...props}
    >
      {coverImage?.desktopImage && (
        <ResponsiveImage
          image={coverImage.desktopImage}
          widths={
            pageType === 'legacyReportPage'
              ? { xs: 425, sm: 768, md: 1024, lg: 1280, xl: 1440, xxl: 2560 }
              : { xs: 1, sm: 1, md: 1024, lg: 1280, xl: 1440, xxl: 2560 }
          }
          className="hidden w-full lg:block"
          dataNotLazy
          preload
        />
      )}
      {coverImage?.mobileImage && (
        <ResponsiveImage
          image={coverImage.mobileImage}
          widths={{ xs: 550, sm: 768, md: 1024 }}
          heights={{ xs: 750, sm: 1024, md: 1334 }}
          dataNotLazy
          preload
          className="lg:hidden"
        />
      )}
      {!coverImage?.desktopImage && !coverImage?.mobileImage && videoType === 'youtube' && (
        <picture className="h-full w-full">
          <source srcSet={`https://i.ytimg.com/vi_webp/${youtubeVideoId}/maxresdefault.webp`} />
          <img
            src={`https://i.ytimg.com/vi/${youtubeVideoId}/maxresdefault.jpg`}
            alt="Video thumbnail"
            className="block h-full w-full object-cover"
          />
        </picture>
      )}

      {(videoEmbedCode || videoUrl || youtubeVideoUrl) && (
        <div className="absolute top-0 left-0 h-full w-full">
          {playClicked ? (
            <>
              {youtubeVideoUrl ? (
                <iframe
                  src={youtubeVideoUrl}
                  allowFullScreen
                  allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture;enablejsapi"
                  className="h-full w-full"
                />
              ) : (
                <EmbeddedMediaElement
                  coverImage={coverImage?.desktopImage}
                  videoEmbedCode={videoEmbedCode}
                  videoUrl={videoUrl}
                  autoplay
                  className="h-full w-full [&_iframe]:h-full [&_iframe]:w-full [&_iframe]:bg-black [&_iframe]:object-cover [&_video]:h-full [&_video]:w-full [&_video]:object-cover"
                />
              )}
            </>
          ) : (
            <div className="flex h-full w-full cursor-pointer items-center justify-center" onClick={_onPlayClicked}>
              {playIcon ? playIcon : <Play className="h-16 w-16" />}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
export default FullPageEmbeddedMediaElement;
